import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../../utils/image';
import './InventoryItemCard.css';

const ShirtIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 2L20 6L17 7V20H7V7L4 6L8 2C8 2 9 4 12 4C15 4 16 2 16 2Z"
          stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BarcodeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 3H5C3.89543 3 3 3.89543 3 5V7M17 3H19C20.1046 3 21 3.89543 21 5V7M3 17V19C3 20.1046 3.89543 21 5 21H7M21 17V19C21 20.1046 20.1046 21 19 21H17M7 12H17"
      stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const formatResolutionReason = (reason) =>
{
  if (!reason) return '';
  return reason
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const getDiffClass = (diff) =>
{
  if (diff < 0) return 'diff-negative';
  if (diff > 0) return 'diff-positive';
  return 'diff-zero';
};

export const InventoryItemCard = ({item, onMarkResolved, onEditActual, readOnly}) =>
{
  const [imageUrl, setImageUrl] = useState(null);
  const [isEditingActual, setIsEditingActual] = useState(false);
  const [actualValue, setActualValue] = useState('');

  useEffect(() =>
  {
    if (item?.style && item?.colorCode)
    {
      getImageUrl(item.style, item.colorCode, 300, 300)
        .then(url => setImageUrl(url))
        .catch(() =>
        {
        });
    }
  }, [item?.style, item?.colorCode]);

  const diff = (item.scannedQuantity ?? 0) - (item.expectedQuantity ?? 0);
  const isResolved = item.status === 'RESOLVED' || item.status === 'OK';
  const statusLabel = item.status;

  const reasonText = item.resolutionReasonDesc
    || formatResolutionReason(item.resolutionReason);

  const handleActualClick = () =>
  {
    if (!isResolved)
    {
      setActualValue((item.scannedQuantity ?? 0).toString());
      setIsEditingActual(true);
    }
  };

  const handleActualChange = (e) =>
  {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value))
    {
      setActualValue(value);
    }
  };

  const handleActualConfirm = () =>
  {
    const newValue = parseInt(actualValue, 10);
    if (!isNaN(newValue) && newValue >= 0)
    {
      setIsEditingActual(false);
      if (onEditActual)
      {
        onEditActual(item.itemId, newValue, item.expectedQuantity ?? 0);
      }
    }
  };

  const handleActualBlur = () =>
  {
    if (actualValue !== '')
    {
      handleActualConfirm();
    } else
    {
      setIsEditingActual(false);
    }
  };

  const handleActualKeyPress = (e) =>
  {
    if (e.key === 'Enter')
    {
      handleActualConfirm();
    } else if (e.key === 'Escape')
    {
      setIsEditingActual(false);
    }
  };

  return (
    <div className={`inv-item-card ${isEditingActual ? 'editing' : ''}`}>
      <div className="inv-item-header">
        {/* Image */}
        <div className="inv-item-image">
          {imageUrl
            ? <img src={imageUrl} alt={item.itemDesc || item.itemId}/>
            : (
              <div className="inv-item-img-placeholder">
                <svg width="40" height="40" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="56" height="56" rx="8" fill="#F3F4F6"/>
                  <path d="M36 14L44 22L38 24V42H18V24L12 22L20 14C20 14 22 18 28 18C34 18 36 14 36 14Z"
                        stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )
          }
        </div>

        <div className="inv-item-header-info">
          {/* Code */}
          <div className="inv-item-code">{item.itemId}</div>

          {/* Description */}
          <div className="inv-item-meta-row">
            <ShirtIcon/>
            <span>{item.itemDesc || '—'}</span>
          </div>

          {/* Barcode */}
          <div className="inv-item-meta-row">
            <BarcodeIcon/>
            <span>{item.barcode || '—'}</span>
          </div>
        </div>
      </div>

      <div className="inv-item-body">
        {/* Attributes */}
        <div className="inv-item-attrs">
          <div className="inv-item-attr">
            <span className="inv-item-attr-label">Size:</span>
            <span className="inv-item-attr-value">{item.sizeDesc || '—'}</span>
          </div>
          <div className="inv-item-attr">
            <span className="inv-item-attr-label">Color:</span>
            <span className="inv-item-attr-value">{item.colorDesc || '—'}</span>
          </div>
          {item.lineDesc && (
            <div className="inv-item-attr">
              <span className="inv-item-attr-label">Tag:</span>
              <span className="inv-item-attr-value">{item.lineDesc}</span>
            </div>
          )}
        </div>

        {/* Quantity management */}
        <div className="inv-item-qty-section">
          <div className="inv-item-qty-header">
            <span className="inv-item-qty-label">Quantity Management</span>
            <span className={`inv-item-status-badge ${isResolved ? 'resolved' : 'pending'}`}>
              {statusLabel}
            </span>
          </div>

          <div className="inv-item-qty-box">
            <div className="inv-item-qty-col">
              <div className="inv-item-qty-col-label">Supposed</div>
              <div className="inv-item-qty-col-value">{item.expectedQuantity ?? 0}</div>
            </div>
            <div className="inv-item-qty-divider"/>
            <div className="inv-item-qty-col">
              <div className="inv-item-qty-col-label">Actual</div>
              {!readOnly && !isResolved && isEditingActual ? (
                <input
                  type="text"
                  className="inv-item-actual-edit"
                  value={actualValue}
                  onChange={handleActualChange}
                  onBlur={handleActualBlur}
                  onKeyDown={handleActualKeyPress}
                  autoFocus
                />
              ) : !readOnly && !isResolved ? (
                <div
                  className="inv-item-qty-col-value editable"
                  onClick={handleActualClick}
                >
                  <div className="inv-item-actual-edit">
                    {item.scannedQuantity ?? 0}
                  </div>
                </div>
              ) : (
                <div className="inv-item-qty-col-value">{item.scannedQuantity ?? 0}</div>
              )}
            </div>
            <div className="inv-item-qty-divider"/>
            <div className="inv-item-qty-col">
              <div className="inv-item-qty-col-label">Difference</div>
              <div className={`inv-item-qty-col-value ${getDiffClass(diff)}`}>
                {diff > 0 ? `+${diff}` : diff}
              </div>
            </div>
          </div>

          {/* Resolution reason (only when manually resolved, not OK) */}
          {item.status === 'RESOLVED' && (
            <div className="inv-item-reason-row">
              <span className="inv-item-reason-label">Reason</span>
              <span className="inv-item-reason-value">
                {reasonText || '—'}
              </span>
            </div>
          )}

          {!isResolved && !readOnly && (
            <button
              className="inv-item-resolve-btn"
              disabled={isResolved}
              onClick={() => onMarkResolved && onMarkResolved(item)}
            >
              Mark as Resolved
            </button>
          )}

        </div>
      </div>
    </div>
  );
};
